//import { parse } from 'tinyduration'
import { number, sum, round, smaller, multiply } from 'mathjs';
import Store from '@/vuex';
import Router from '@/router/router';
import Vue from 'vue';
import VueWebStorage from 'vue-web-storage';
import { rybbit_event_from_custom } from '@/utilities/integrations/rybbit';
import { gtm_track_event } from '@/utilities/integrations/gtm';
import { clarity_set_variable } from '@/utilities/integrations/clarity';
import { logrocket_track } from '@/utilities/integrations/logrocket';

Vue.use(VueWebStorage, {
  prefix: '', // default `app_`
  drivers: ['session', 'local'], // default 'local'
});

export function log(...value) {
  if (process.env.VUE_APP_MODE !== 'development') return;
  window.console.log(...value);
}

const pad = (s) => (s < 10 ? `0${s}` : s);

/**
 * Ritorna true se il valore è "impostato", cioè diverso sia da null che da undefined.
 * Va preferito ai controlli `=== null` / `=== undefined`, che intercettano solo uno
 * dei due casi (un `=== null` lascia passare undefined e viceversa).
 * @param {*} value
 * @returns {boolean}
 */
export const is_set = (value) => value !== null && value !== undefined;

export function is_development() {
  if (process.env.VUE_APP_MODE === 'development') return true;
  if (process.env.VUE_APP_MODE === 'staging') return true;
  return false;
}

export function show_developer_tools() {
  return (
    is_development() ||
    get_config('guiSettings.customizations.showDevTools', false)
  );
}

export function is_authenticated() {
  let { token } = Store.state;

  // se c'è un token in get ha la precedenza (quello nello state potrebbe essere scaduto)
  if (Router.history.current.query.token) {
    token = Router.history.current.query.token;
  }

  // non ho trovato alcun token
  if (!token) return false;

  // ho un token ma NON un'integrazione

  // implementazione cancellara -> integration nello state
  // if(!Store.state.integration || !Store.state.integration.integration) return false

  // implementazione livetravel -> integration solo nel config
  if (!get_config('integration.integration')) return false;

  return true;
}

export function get_config(path = '', fallback = false) {
  let cur = { ...Vue.prototype.$config };

  if (!cur) return fallback;
  if (!path) return cur;

  for (const key of path.split('.')) {
    if (!is_set(cur) || typeof cur !== 'object') return fallback;
    cur = cur[key];
  }

  return is_set(cur) ? cur : fallback;
}

export function get_availability(price) {
  const _price = price || 0;

  const config = get_config();
  const { discount_codes } = Store.state;
  const availability = {
    total: 0,
    welfare: false,
    is_known: false,
    details: {
      integration: 0,
      discount_codes: 0,
    },
  };

  if (!is_set(config)) return availability;
  if (!is_set(config.integration)) return availability;

  const is_jointly = config.integration.integration === 'jointly';

  /**
   * cia: config.integration.availability può arrivare -1 ad indicare che il credito non è conosciuto
   * availability.is_known: per tracciare se la disponibilità è conosciuta oppure no
   */
  const cia = round(number(config.integration.availability), 2);
  const real_availability = cia > 0 ? cia : 0;

  availability.total = real_availability;

  availability.details.integration = real_availability;
  availability.welfare = config.integration.welfare ? true : false;
  availability.is_known = cia > -1 ? true : false;

  if (discount_codes.length) {
    discount_codes.forEach((dc) => {
      const value_resodual = number(dc.valueResidual);
      availability.details.discount_codes = sum(
        availability.details.discount_codes + value_resodual
      );
      availability.total = sum(availability.total + value_resodual);
    });
  }

  if (get_to_points(availability.total)) {
    availability.points = get_to_points(availability.total);
  }

  /**
   * affordable:
   * non viene più ritornato, va calcolato internamente per determinare availability.can_go_to_checkout (vedi sotto)
   */
  let affordable = true;
  if (_price > 0 && availability.is_known && !is_jointly) {
    if (smaller(availability.total, _price)) affordable = false;
  }

  /*
   * can_go_to_checkout (ex "can_checkout"):
   * - credito (totale) non sufficiente + welfare:false => POSSO andare al checkout ed integrare con carta di credito
   * - credito (totale) non sufficiente + welfare:true => NON POSSO andare al checkout
   */
  availability.can_go_to_checkout =
    is_jointly || !availability.welfare || affordable;

  /*
   * show_credit_alert (ex "affordable"):
   * - sostituisce il vecchio availability.affordable, che ora NON viene ritornato
   * - pilota copy "Credito non sufficiente" a front
   * - RIAGGANCIATO a logiche di prezzo, cioè a .can_go_to_checkout (per coerenza con tasto "Prenota" abilitato o meno)
   */
  availability.show_credit_alert = !availability.can_go_to_checkout;

  return availability;
}

export function get_to_points(amount) {
  /**
   * cgcp: config.guiSettings.customizations.points indica una customizzazione con i punti
   * points_rate: percentuale di conversione in punti
   * points: punti calcolati (arrotondati all'intero più vicino)
   */

  // usa il getter dello store per verificare la disponibilità dei punti
  if (!Store.getters.points_available) return null;
  // silent bug: al primo render in /detail amount era undefined e rompeva heading.vue
  if (!is_set(amount)) return null;

  const cgcp = Vue.prototype.$config.guiSettings.customizations.points;
  const points_rate = number(cgcp.availabilityPointsRate || 0);
  const points = round(multiply(amount, points_rate));

  return {
    ...cgcp,
    total: points,
  };
}

export function parse_date(date) {
  // Safety guard on valid date
  if (!date || !date.split) {
    return;
  }
  const [date_part, time_part] = date.split('T');
  const [year, month_number, day_number] = date_part.split('-');

  const new_date = new Date(year, parseInt(month_number, 10) - 1, day_number);
  const month_string = new_date.toLocaleString('default', { month: 'long' });

  const hour = time_part ? time_part.slice(0, 5) : time_part; // rimuove i secondi (:00)
  return {
    day: date_part,
    hour,
    day_number,
    month_number,
    year,
    month_string,
    readable_datetime: `${day_number}-${month_number}-${year} ${hour}`,
    friendly_date: `${day_number} ${month_string} ${year}`,
    friendly_date_short: `${day_number} ${month_string.slice(0, 3).toUpperCase()}`,
  };
}

export function reverse_date(date) {
  // yyyy-mm-dd to dd-mm-yyyy and viceversa

  //return date.split('-').reverse().join('-');

  if (!date) return '';

  let dateToUse = date;

  if (date.indexOf('T') === -1) dateToUse = `${date}T00:00:00`; // se non c'è l'orario viene applicato il fuso dell'utente

  const d = new Date(dateToUse);

  const reversed = [
    pad(d.getDate()),
    pad(d.getMonth() + 1),
    d.getFullYear(),
  ].join('-');

  return reversed;
}

export function custom_event(action, label, value, category) {
  let _action = action;
  const _label = label || '';
  const _value = value || 0;
  const _category = category || '';

  // GTM: standard payload OR pass-through GA4 ecommerce event
  gtm_track_event(_action, _label, _value, _category);

  // Rybbit: normalized custom_event → Rybbit event.
  rybbit_event_from_custom(_action, _label, _value, _category);

  // Below this point `action` must be a string (GA4 events arrive as objects)
  if (typeof _action === 'object') _action = _action.event;

  // Clarity: set custom variable
  clarity_set_variable(_action, _label);

  // LogRocket: track event by composed name
  logrocket_track(`${_action}_${_label}`);
}

export function encode_parameters(params) {
  return btoa(encodeURIComponent(JSON.stringify(params)));
}

export function decode_parameters(params) {
  return JSON.parse(decodeURIComponent(atob(params)));
}

export function deep_clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function is_plain_object(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function same_value(a, b) {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return (
      a.length === b.length &&
      a.every((item, index) => same_value(item, b[index]))
    );
  }
  if (is_plain_object(a) && is_plain_object(b)) {
    const keys_a = Object.keys(a).sort();
    const keys_b = Object.keys(b).sort();
    return (
      same_value(keys_a, keys_b) &&
      keys_a.every((key) => same_value(a[key], b[key]))
    );
  }
  return false;
}

const is_unset = (obj) => !is_set(obj);

/**
 * Safely access a nested property of an object using a dot-separated path.
 * Returns fallback if any segment along the path is null/undefined.
 *
 * @param {Object} obj - The source object
 * @param {string} path - Dot-separated path (e.g. "quote_payload.roomDescription")
 * @param {*} [fallback=""] - Value to return if the path does not resolve
 * @param {*} [separator=""] - Value to concatenate nested paths with
 * @returns {*} The resolved value or fallback
 */
export const get_object_field = (
  obj,
  field,
  fallback = undefined,
  separator = '.'
) => {
  if (!obj || !field) {
    return fallback;
  }
  if (!field.includes(separator)) {
    return is_unset(obj[field]) ? fallback : obj[field];
  }
  const [current_level, ...levels] = field.split(separator);
  return get_object_field(
    obj[current_level],
    levels.join(separator),
    fallback,
    separator
  );
};

/**
 * Extract multiple nested fields from an object into a flat key-value map.
 * Each path's last segment becomes the output key.
 *
 * @param {Object} obj - The source object
 * @param {string[]} paths - Array of dot-separated paths
 * @param {*} [_fallback=""] - Default value for missing paths
 * @returns {Object} Flat object with last-segment keys mapped to resolved values
 */
export function get_object_fields(obj, paths, fallback) {
  let _fallback = fallback;

  if (_fallback === undefined) _fallback = '';
  const result = {};
  if (!is_set(obj) || typeof obj !== 'object' || !Array.isArray(paths)) {
    return result;
  }
  for (let i = 0; i < paths.length; i++) {
    const path = paths[i];
    const segments = path.split('.');
    const key = segments[segments.length - 1];
    result[key] = get_object_field(obj, path, _fallback);
  }
  return result;
}

export function is_mobile() {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    return true;
  }
  return false;
}

export function format_date_YYYY_MM_DD(date = new Date(), separator = '') {
  // nested arrow function
  const pad_to_2_digits = (num) => num.toString().padStart(2, '0');
  return [
    date.getFullYear(),
    pad_to_2_digits(date.getMonth() + 1),
    pad_to_2_digits(date.getDate()),
  ].join(separator);
}

export function get_traveler_birth_range_dates(departure, pax_age) {
  // formato di ritorno richiesto dal picker: 2024-03-20
  let _pax_age = pax_age;

  let min = new Date(departure);
  let max = new Date(departure);

  if (_pax_age >= 30) _pax_age = 12; // alpitour adults default 30
  // facendo il range in accordo con il tipo pax
  // si crea un conflitto con i CHD considerati ADT (che ritornatno ADT ed age 30)
  // rendendo impossibile indicare la data di nascita
  // come fix, nel caso ADT, considero 12 anni (che sono sempre CHD):
  //   a noi interessa rendere facile per l'utente mettere le date di nascita di chd e inf, che resta invariato
  //   nel caso di ADT, potrebbe essere possibile inserire date nascita "da minorenni"... pazienza

  min = new Date(min.setFullYear(1900));
  max = new Date(max.setFullYear(max.getFullYear() - _pax_age));
  max.setDate(max.getDate() - 2); // evita che il pax compia gli anni nei 2 giorni prima della partenza

  min = format_date_YYYY_MM_DD(min, '-');
  max = format_date_YYYY_MM_DD(max, '-');

  return {
    min,
    max,
  };
}

/**
 * Calculate birth date constraints based on declared age at a target date.
 * Used in the only-stay (sololand) flow for passenger birth date pickers.
 *
 * @param {Object} params
 * @param {string} [params.target_date] - Reference date (checkin). Defaults to today.
 * @param {boolean} [params.adult] - If true, returns broad adult range (born at least 18y before target).
 * @param {number} [params.age] - Declared age. Constrains range to exactly this age at target_date.
 *   maxDate = target - age years (turns exactly `age` on target day)
 *   minDate = target - (age+1) years + 1 day (still `age` on target, turns age+1 the day after)
 *
 * Graceful degradation: invalid/missing age with adult=false returns a generic fallback range.
 */
export const get_birth_date_constraints = (params) => {
  const { target_date, adult, age } = params || {};
  const ref = target_date ? new Date(target_date) : new Date();
  const fallback = {
    min: format_date_YYYY_MM_DD(new Date(1900, 0, 1), '-'),
    max: format_date_YYYY_MM_DD(ref, '-'),
  };

  if (isNaN(ref.getTime())) {
    return fallback;
  }

  const year = ref.getFullYear();
  const month = ref.getMonth();
  const day = ref.getDate();

  if (adult) {
    return {
      min: fallback.min,
      max: format_date_YYYY_MM_DD(new Date(year - 18, month, day), '-'),
    };
  }

  const parsed_age =
    typeof age === 'number' && isFinite(age) && age >= 0 ? age : null;

  if (parsed_age === null) {
    return fallback;
  }

  const max = new Date(year - parsed_age, month, day);
  const min = new Date(year - (parsed_age + 1), month, day);
  // When the target day doesn't exist in the min year (e.g. Feb 29 in a non-leap year),
  // JS Date auto-adjusts forward (to Mar 1). In that case, skip the +1 day to avoid overshooting.
  const date_overflowed = min.getMonth() !== month;
  if (!date_overflowed) {
    min.setDate(min.getDate() + 1);
  }

  return {
    min: format_date_YYYY_MM_DD(min, '-'),
    max: format_date_YYYY_MM_DD(max, '-'),
  };
};

export function format_price(price) {
  let _price = price;

  if (_price.length === 1) {
    return _price.replace(/\D+/g, '') ? _price.replace(/\D+/g, '') : 0;
  }

  const currency = '€';
  let separator = '';
  let price_split = [];

  if (_price.includes(',')) {
    _price = _price.replace(/[^\d,]/g, '');
    separator = ',';
    price_split = _price.split(separator);
  } else if (_price.includes('.')) {
    _price = _price.replace(/[^\d.]/g, '');
    separator = '.';
    price_split = _price.split(separator);
  } else {
    _price = _price.replace(/\D+/g, '');
  }

  if (price_split.length > 1) {
    _price =
      price_split[0] +
      separator +
      price_split[1].replace(/\D+/g, '').slice(0, 2);
  }

  if (_price.length) return `${currency} ${_price}`;

  return '';
}

export function get_type_icon(type = '') {
  switch (type) {
    case 'hotel':
    case 'property':
      return 'bed_left';

    case 'city':
      return 'city';

    case 'district':
    case 'subdistrict':
      return 'district';

    case 'region':
      return 'region';

    case 'country':
      return 'country';

    case 'airport':
      return 'airplane';

    case 'landmark':
      return 'landmark';

    case 'map':
      return 'map';

    case 'point':
      return 'map';

    default:
      return 'pin';
  }
}

export function get_site_name() {
  return `${this.get_config('site.brand', '')} ${this.get_config('site.payoff', '')}`;
}

// Map with components allowed to be inserted between result pads
const ALLOWED_EXTRA_COMPONENTS = {
  'marketing-pad': true,
  img: true,
};

// Map with condition key and the related check
const COMPONENTS_CONDITIONS = {
  hasWelfare: (v) => get_config('integration.welfare') === v,
  // Loyalty -> customization loyalty + welfare:false
  hasLoyalty: (v) => {
    if (get_config('integration.welfare')) {
      return !v;
    }
    const points = get_config('guiSettings.customizations.points', {});
    return Object.keys(points).length > 0 === v;
  },
  // Convenzioni -> customization convenzioni + welfare:false
  hasConventions: (v) => {
    if (get_config('integration.welfare')) {
      return !v;
    }
    const loyalty = [
      'showValueAdds',
      'showDeal',
      'strikethrough',
      'sennaFilters',
    ].every((key) => {
      const value = get_config(`guiSettings.customizations.${key}`);
      if (!value) {
        return false;
      }
      if (typeof value === 'boolean') {
        return value;
      }
      return Object.keys(value).length > 0;
    });
    return loyalty === v;
  },
};

export const add_extra_components_to_results = (
  results = [],
  id_field = 'id'
) => {
  try {
    // Sanity check
    if (!results || results.length === 0) {
      return results;
    }

    const {
      items: config_items = [],
      positions: config_positions = [],
      repeat = false,
    } = get_config('guiSettings.customizations.resultsExtraComponents', {});

    // Sanity check: informations must be correct to render
    if (config_items.length === 0 || config_positions.length === 0) {
      return results;
    }

    // Filter invalid positions
    const positions = config_positions
      .filter((p) => !isNaN(p))
      .map(Number)
      .filter((p) => p > 0);
    if (positions.length === 0) {
      return results;
    }

    // Filter invalid components
    const items = config_items
      .filter((item) => Object.keys(item || {}).length > 0)
      .filter((item) => ALLOWED_EXTRA_COMPONENTS[String(item.component)])
      .filter((item) => {
        if (!item.conditions) {
          return true;
        }
        return Object.entries(item.conditions).every((entry) => {
          const [conditions, params = []] = entry;
          return conditions.split('|').some((condition) => {
            const validator = COMPONENTS_CONDITIONS[condition];
            return !validator || validator(...params);
          });
        });
      });

    if (items.length === 0) {
      return results;
    }
    const injected = [...results];

    let result_relative_index = 0;
    let item_index = 0;
    let position_index = 0;
    for (
      let result_index = 0;
      result_index < results.length;
      result_index += 1
    ) {
      // Insert after every N items (but not after the last item)
      if (
        result_relative_index === positions[position_index] &&
        result_index !== results.length - 1
      ) {
        injected.splice(result_index, 0, {
          [id_field]: `result-extra:${result_index}`,
          ...items[item_index],
        });
        item_index += 1;
        position_index += 1;
        result_relative_index = 0;
      } else {
        result_relative_index += 1;
      }

      if (position_index >= positions.length) {
        position_index = 0;
      }

      if (item_index >= items.length) {
        if (!repeat) {
          return injected;
        }
        item_index = 0;
      }
    }

    return injected;
  } catch (e) {
    // If anything goes wrong fallbackto normal resutls with no injected components
    this.log('ERROR: add_extra_components_to_results', e);
    return results;
  }
};
